<template>
  <BaseLayout>
    <div class="card">
      <div class="pb-4 rounded-top">
        <div class="container py-12 px-xl-10 px-4">
          <h3 class="text-center mb-2 mt-0 mt-md-4">Доступные тарифы</h3>
          <p class="text-center mb-0">
            Выберите подходящий тариф для вашего бизнеса
          </p>

          <LoadingBlock :loading="loading">
            <div v-if="!loading && tariffs.length === 0" class="text-center py-5">
              <p class="text-muted">Тарифы не найдены</p>
            </div>

            <div v-else class="row mx-0 px-lg-12 mt-9">
              <div 
                v-for="tariff in tariffs" 
                :key="tariff.id" 
                class="col-xl mb-xl-0 mb-6"
              >
                <div class="card border rounded shadow-none h-100">
                  <div class="card-body pt-12 px-5 d-flex flex-column">
                    <div class="mt-3 mb-5 text-center">
                      <i class="ti ti-credit-card text-primary" style="font-size: 5rem;"></i>
                    </div>
                    <h4 class="card-title text-center text-capitalize mb-1">{{ tariff.name }}</h4>
                    <div class="text-center mb-5" style="min-height: 3rem; display: flex; align-items: center; justify-content: center;">
                      <p class="mb-0">{{ tariff.description || 'Тарифный план' }}</p>
                    </div>
                      <div class="text-center h-px-50">
                        <div>
                          <h1 class="mb-0 text-primary">
                            <span v-if="tariff.price === 0 || tariff.price === null" class="h5 text-muted">Бесплатно</span>
                            <span v-else>{{ formatPrice(tariff.price) }}</span>
                          </h1>
                          <div class="h6 text-body" v-if="tariff.price > 0">₽/месяц</div>
                        </div>
                      </div>

                    <div class="flex-grow-1 d-flex flex-column">
                      <ul class="list-group ps-6 my-5 pt-9 flex-grow-1">
                        <li v-for="limit in getSortedLimits(tariff.limits)" :key="limit.limit_type" class="mb-4">
                          {{ getLimitTypeName(limit.limit_type) }}: <strong>{{ formatNumber(limit.quantity) }}</strong>
                        </li>
                        <li v-if="!tariff.limits || tariff.limits.length === 0" class="mb-0 text-muted">
                          Лимиты не указаны
                        </li>
                      </ul>

                      <div v-if="isCurrentTariff(tariff)" class="text-center py-2">
                        <span class="text-muted">Текущий тариф</span>
                      </div>
                      <div v-else>
                        <router-link :to="{ path: '/payment', query: { tariff_id: tariff.id } }" class="btn btn-primary d-grid w-100 mb-2">
                          Выбрать тариф
                        </router-link>
                        <button 
                          v-if="shouldShowTrial(tariff)" 
                          @click="activateTrialForTariff(tariff)"
                          :disabled="activatingTrial"
                          class="btn btn-link text-muted small p-0 w-100 border-0 text-decoration-underline"
                          style="font-size: inherit;"
                        >
                          Включить пробный период 14 дней
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </LoadingBlock>
        </div>
      </div>
    </div>
  </BaseLayout>
</template>

<script>
import BaseLayout from "@/layouts/Base.vue";
import {PageInfo} from "@/data/page";
import {user} from "@/data/user";
import {subscription} from "@/data/subscription";
import {getActiveSubscriptions, activateTrial} from "@/api/subscription";
import LoadingBlock from "@/components/page/LoadingBlock.vue";

export default {
  name: 'TariffsView',
  components: {BaseLayout, LoadingBlock},
  data() {
    return {
      tariffs: [],
      loading: true,
      currentTariffId: null,
      activatingTrial: false
    }
  },
  computed: {
    subscriptionInfo() {
      return subscription.subscription_info
    }
  },
  methods: {
    async loadTariffs() {
      this.loading = true
      try {
        const result = await getActiveSubscriptions()
        const tariffs = result.data || []
        // Сортируем по возрастанию цены (бесплатные тарифы в начале)
        this.tariffs = tariffs.sort((a, b) => {
          const priceA = a.price === null || a.price === undefined ? 0 : a.price
          const priceB = b.price === null || b.price === undefined ? 0 : b.price
          return priceA - priceB
        })
      } catch (error) {
        console.error('Ошибка загрузки тарифов:', error)
        this.tariffs = []
      } finally {
        this.loading = false
      }
    },
    formatPrice(price) {
      if (price === 0 || price === null || price === undefined) {
        return ''
      }
      return price.toLocaleString('ru-RU')
    },
    formatNumber(number) {
      if (number === null || number === undefined) return '0'
      return number.toLocaleString('ru-RU')
    },
    getLimitTypeName(limitType) {
      const limitTypes = {
        'product': 'Товары',
        'store': 'Магазины',
        'strategy': 'Стратегии',
        'user': 'Пользователи',
        'tracked_offers': 'Цены на витрине'
      }
      return limitTypes[limitType] || limitType
    },
    getSortedLimits(limits) {
      if (!limits || !Array.isArray(limits)) {
        return []
      }
      const order = ['product', 'store', 'strategy', 'user', 'tracked_offers']
      return limits.slice().sort((a, b) => {
        const indexA = order.indexOf(a.limit_type)
        const indexB = order.indexOf(b.limit_type)
        // Если тип не найден в порядке, ставим в конец
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
      })
    },
    isCurrentTariff(tariff) {
      if (!this.subscriptionInfo || !this.subscriptionInfo.subscription) {
        return false
      }
      return this.subscriptionInfo.subscription.id === tariff.id
    },
    shouldShowTrial(tariff) {
      // Не показываем для текущего тарифа
      if (this.isCurrentTariff(tariff)) {
        return false
      }
      
      // Показываем только для платных тарифов
      if (!tariff.price || tariff.price === 0 || tariff.price === null) {
        return false
      }
      
      // Проверяем, использован ли trial у текущего тарифа
      if (this.subscriptionInfo && this.subscriptionInfo.subscription) {
        // Если trial_used === false или не задан (undefined/null), значит trial не использован
        const trialUsed = this.subscriptionInfo.subscription.trial_used
        return trialUsed === false || trialUsed === undefined || trialUsed === null
      }
      
      return false
    },
    async activateTrialForTariff(tariff) {
      if (this.activatingTrial) {
        return
      }
      
      this.activatingTrial = true
      try {
        const activeCompany = await user.getActiveCompany()
        if (!activeCompany) {
          alert('Не выбрана активная компания')
          return
        }
        
        await activateTrial(activeCompany, tariff.id)
        
        // Обновляем информацию о подписке
        await subscription.getSubscriptionInfo(true)
        
        // Перезагружаем тарифы
        await this.loadTariffs()
        
        alert('Пробный период успешно активирован')
      } catch (error) {
        console.error('Ошибка активации пробного периода:', error)
        alert('Ошибка активации пробного периода: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.activatingTrial = false
      }
    },
    setPageInfo() {
      PageInfo.title = 'Тарифы'
      PageInfo.breadcrumbs = [
        {
          title: 'Главная',
          link: '/'
        },
        {
          title: PageInfo.title
        }
      ]
    }
  },
  async mounted() {
    this.setPageInfo()
    await user.getUserBase()
    await subscription.getSubscriptionInfo()
    await this.loadTariffs()
  }
}
</script>

<style scoped>
.h-px-50 {
  height: 50px;
}
</style>

